import {
  memo,
  type ReactNode,
  type SyntheticEvent,
} from 'react';
import * as Popover from '@radix-ui/react-popover';
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  INFO_EXPLAIN_ICON_WELL_CLASS,
  INFO_EXPLAIN_PANEL_HEADER_CLASS,
  INFO_EXPLAIN_TRIGGER_CLASS,
} from './infoExplainChrome.js';
import { INFO_EXPLAIN_METRIC_PANEL_CLASS } from './infoExplainPopoverChrome.js';
import { InfoExplainPopoverPortal } from './InfoExplainPopoverShell.js';
import { useInfoExplainPopoverState } from './useInfoExplainPopoverState.js';

export interface MetricExplainButtonProps {
  readonly title: string;
  /** Plain-language: what this number/panel is. */
  readonly what: string;
  /** Why an operator should care. */
  readonly why?: string;
  /** What to do when the value looks bad. */
  readonly action?: string;
  readonly testId?: string;
  readonly className?: string;
}

type SectionTone = 'info' | 'why' | 'action';

const SECTION_TONE: Record<
  SectionTone,
  {
    readonly iconWell: string;
    readonly rail: string;
    readonly surface: string;
  }
> = {
  info: {
    iconWell: INFO_EXPLAIN_ICON_WELL_CLASS,
    rail: 'border-[var(--color-an-border)]',
    surface: 'bg-[var(--color-an-bg-elevated)]',
  },
  why: {
    iconWell:
      'bg-[color-mix(in_srgb,var(--color-an-warn)_18%,var(--color-an-surface))] text-[var(--color-an-warn)]',
    rail: 'border-[color-mix(in_srgb,var(--color-an-warn)_55%,var(--color-an-border))]',
    surface:
      'bg-[color-mix(in_srgb,var(--color-an-warn)_6%,var(--color-an-surface))]',
  },
  action: {
    iconWell:
      'bg-[color-mix(in_srgb,var(--color-an-success)_18%,var(--color-an-surface))] text-[var(--color-an-success)]',
    rail: 'border-[color-mix(in_srgb,var(--color-an-success)_55%,var(--color-an-border))]',
    surface:
      'bg-[color-mix(in_srgb,var(--color-an-success)_6%,var(--color-an-surface))]',
  },
};

function ExplainSection({
  tone,
  label,
  body,
  icon,
}: {
  readonly tone: SectionTone;
  readonly label: string;
  readonly body: string;
  readonly icon: ReactNode;
}): JSX.Element {
  const styles = SECTION_TONE[tone];
  return (
    <div
      className={`flex gap-2.5 rounded-lg border-l-[3px] ${styles.rail} ${styles.surface} px-2.5 py-2`}
    >
      <span
        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${styles.iconWell}`}
        aria-hidden
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="m-0 text-[length:var(--badge-font-size-sm)] font-semibold tracking-normal text-[var(--color-an-text-muted)] normal-case">
          {label}
        </p>
        <p className="m-0 mt-0.5 text-[length:var(--font-size-small)] leading-[var(--line-height-small)] text-[var(--color-an-text)] [text-wrap:pretty]">
          {body}
        </p>
      </div>
    </div>
  );
}

/**
 * Click “i” help for metrics and panels — teaches non-experts in-place.
 * Portals to `document.body` so list cards / DetailDrawer footers / BN cannot
 * paint through or over the panel (mobile stacking). Modal + scrim focus the
 * explain; collision padding clears TEST chrome + bottom nav + safe-area.
 * Trigger chrome is muted B/W tokens (`infoExplainChrome`) — not chart-1 blue.
 */
export const MetricExplainButton = memo<MetricExplainButtonProps>(
  ({ title, what, why, action, testId, className = '' }) => {
    const { t } = useTranslation('admin');
    const { open, collisionPadding, handleOpenChange } =
      useInfoExplainPopoverState();

    if (!what.trim()) {
      return null;
    }

    const whyText = why?.trim() ?? '';
    const actionText = action?.trim() ?? '';
    const rootClass =
      className.length > 0
        ? `relative inline-flex shrink-0 items-center ${className}`
        : 'relative inline-flex shrink-0 items-center';
    const panelTestId =
      testId != null ? `${testId}-panel` : 'metric-explain-panel';
    const scrimTestId =
      testId != null ? `${testId}-scrim` : 'metric-explain-scrim';

    /** Parent KPI cards are often wrapped in `<Link>`; keep explain clicks local. */
    const stopLinkActivation = (event: SyntheticEvent): void => {
      event.stopPropagation();
    };

    return (
      <Popover.Root modal open={open} onOpenChange={handleOpenChange}>
        <span className={rootClass}>
          <Popover.Trigger asChild>
            <button
              type="button"
              className={INFO_EXPLAIN_TRIGGER_CLASS}
              aria-label={t('metricExplain.aria', { title })}
              data-testid={testId ?? 'metric-explain-button'}
              onClick={stopLinkActivation}
              onPointerDown={stopLinkActivation}
            >
              <Info className="h-4 w-4" aria-hidden />
            </button>
          </Popover.Trigger>
          <InfoExplainPopoverPortal scrimTestId={scrimTestId}>
            <Popover.Content
              role="dialog"
              aria-label={title}
              side="bottom"
              align="center"
              sideOffset={8}
              collisionPadding={collisionPadding}
              avoidCollisions
              data-export-exclude="chrome"
              data-testid={panelTestId}
              className={INFO_EXPLAIN_METRIC_PANEL_CLASS}
              onClick={stopLinkActivation}
              onPointerDown={stopLinkActivation}
              onOpenAutoFocus={(event) => {
                event.preventDefault();
              }}
            >
              <div className={`${INFO_EXPLAIN_PANEL_HEADER_CLASS} shrink-0`}>
                <span
                  className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${INFO_EXPLAIN_ICON_WELL_CLASS}`}
                  aria-hidden
                >
                  <Info className="h-4 w-4" />
                </span>
                <p className="m-0 min-w-0 flex-1 pt-0.5 text-[length:var(--font-size-h5)] font-semibold leading-[var(--line-height-h5)] tracking-tight text-[var(--color-an-text)] [text-wrap:pretty]">
                  {title}
                </p>
                <Popover.Close asChild>
                  <button
                    type="button"
                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[var(--color-an-text-muted)] hover:bg-[var(--color-an-bg-elevated)] hover:text-[var(--color-an-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-an-focus-ring)]"
                    aria-label={t('metricExplain.close')}
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </button>
                </Popover.Close>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                <div className="flex flex-col gap-2 px-3.5 py-3">
                  <ExplainSection
                    tone="info"
                    label={t('metricExplain.what')}
                    body={what}
                    icon={<Info className="h-3.5 w-3.5" />}
                  />
                  {whyText.length > 0 ? (
                    <ExplainSection
                      tone="why"
                      label={t('metricExplain.why')}
                      body={whyText}
                      icon={<CircleAlert className="h-3.5 w-3.5" />}
                    />
                  ) : null}
                  {actionText.length > 0 ? (
                    <ExplainSection
                      tone="action"
                      label={t('metricExplain.action')}
                      body={actionText}
                      icon={<CircleCheck className="h-3.5 w-3.5" />}
                    />
                  ) : null}
                </div>
              </div>

              <div className="shrink-0 border-t border-[var(--color-an-border)] bg-[var(--color-an-surface)] px-3.5 py-2.5">
                <Popover.Close asChild>
                  <button
                    type="button"
                    data-testid={testId ? `${testId}-close` : undefined}
                    className="inline-flex min-h-11 w-auto max-w-[min(100%,14rem)] self-start items-center justify-center rounded-md border border-[var(--color-an-border)] bg-[var(--color-an-bg-elevated)] px-3 text-xs font-semibold text-[var(--color-an-text)] hover:border-[var(--color-an-border-hover,var(--color-an-border))] hover:bg-[var(--color-an-surface-hover,var(--color-an-bg-elevated))] hover:text-[var(--color-an-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-an-focus-ring)]"
                  >
                    {t('metricExplain.close')}
                  </button>
                </Popover.Close>
              </div>
            </Popover.Content>
          </InfoExplainPopoverPortal>
        </span>
      </Popover.Root>
    );
  },
);

MetricExplainButton.displayName = 'MetricExplainButton';
